import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import TeamMemberDigitalCardPreview from 'team/components/TeamMemberDigitalCardPreview';
import TeamTemplateQRCodePreview from './TeamTemplateQRCodePreview';
import { useTeamTemplateContext } from 'team/contexts/TeamTemplateContext';

const TeamTemplatePreview = ({ selectedItem, template }) => {
    const { templateState } = useTeamTemplateContext();
    const templateId = template?._id || 'new-template';

    // Use context state if available (for real-time updates), otherwise use prop
    // Context state is updated immediately when user types, prop might lag
    const currentTemplate = templateState[templateId] || template;

    // Convert template data to card data format for preview
    // CRITICAL: Get about directly from currentTemplate to ensure we get the latest updates
    const about = currentTemplate?.about || {};
    const qrCode = currentTemplate?.qrCode || {};

    // Debug logging for company logo - watch the entire currentTemplate to catch updates
    React.useEffect(() => {
        const stateAbout = templateState[templateId]?.about;
        console.log('[TeamTemplatePreview] 🔍 Company logo URL check:', {
            templateId,
            currentTemplateId: currentTemplate?._id,
            companyLogoUrl: about.companyLogoUrl,
            stateCompanyLogoUrl: stateAbout?.companyLogoUrl,
            fullAbout: about,
            currentTemplateAbout: currentTemplate?.about,
            templateStateAbout: stateAbout,
            areEqual: about.companyLogoUrl === stateAbout?.companyLogoUrl,
            templateStateObject: templateState[templateId]
        });
    }, [about.companyLogoUrl, templateId, currentTemplate?._id, currentTemplate?.about?.companyLogoUrl, templateState, templateState[templateId]?.about?.companyLogoUrl]);

    // Create card data object from template - memoized to prevent unnecessary re-renders
    // CRITICAL: Include all specific fields in dependencies to ensure real-time updates
    // This matches the card update pattern where preview updates immediately as user types
    // IMPORTANT: Watch templateState object itself to catch any updates (React will detect object reference changes)
    const cardData = useMemo(() => {
        // Get the latest about from templateState directly to ensure we get the latest updates
        const stateAbout = templateState[templateId]?.about || currentTemplate?.about || {};
        const data = {
            cardFullName: stateAbout.cardName || currentTemplate?.templateName || 'Template Preview',
            cardJobTitle: '',
            cardCompany: stateAbout.company || '',
            cardPronoun: stateAbout.pronoun || 'he',
            cardTheme: stateAbout.cardTheme || '#FF7A00',
            cardProfilePictureUrl: stateAbout.profilePictureUrl || '',
            cardCoverPhotoUrl: stateAbout.coverPhotoUrl || '',
            cardCompanyLogoUrl: stateAbout.companyLogoUrl || null,

            cardLinkColor: stateAbout.linkColor || '#FF7A00',
            cardFont: stateAbout.font || 'Inter',
            cardLocation: stateAbout.location || '',
            cardBio: stateAbout.bio || '',
            customLinkTypeLabels: currentTemplate?.links?.customLinkTypeLabels || {}
        };
        console.log('[TeamTemplatePreview] 🔍 cardData memoized:', {
            cardCompanyLogoUrl: data.cardCompanyLogoUrl,
            stateAboutCompanyLogoUrl: stateAbout.companyLogoUrl,
            stateAbout: stateAbout,
            templateStateAbout: templateState[templateId]?.about,
            templateState: templateState
        });
        return data;
    }, [
        // CRITICAL: Watch templateState object itself - when context updates, the object reference changes
        // This is the most reliable way to catch all updates including companyLogoUrl
        templateState,
        templateId,
        currentTemplate?.templateName,
        currentTemplate?.links?.customLinkTypeLabels
    ]);

    return (
        <Box sx={{ height: '100%', overflow: 'auto' }}>
            {/* QR Code Preview */}
            {selectedItem === 'qrCode' && (
                <Box sx={{ display: 'block', height: '100%' }}>
                    <TeamTemplateQRCodePreview qrCode={qrCode} />
                </Box>
            )}

            {/* Card Preview for all other sections */}
            {selectedItem !== 'qrCode' && (
                <Box sx={{ display: 'block', height: '100%' }}>
                    <TeamMemberDigitalCardPreview
                        cardId="template-preview"
                        cardData={cardData}
                        contactLinks={[]}
                        disableLeadCapture={true}
                    />
                </Box>
            )}
        </Box>
    );
};

export default TeamTemplatePreview;

